import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, LogIn } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, telHref } from "@/lib/api";

const NAV = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/hizmetlerimiz", label: "Hizmetlerimiz", menu: "services" },
  { to: "/sektorler", label: "Sektörler", menu: "sectors" },
  { to: "/kalite-ve-guvenlik", label: "Kalite ve Güvenlik" },
  { to: "/kariyer", label: "Kariyer" },
  { to: "/blog", label: "Blog" },
  { to: "/iletisim", label: "İletişim" },
];

const linkCls = ({ isActive }) =>
  `relative whitespace-nowrap px-2.5 py-2 text-[13px] font-semibold transition-colors xl:px-3 ${isActive ? "text-white" : "text-zinc-300 hover:text-white"}`;

function Dropdown({ items }) {
  return (
    <div className="invisible absolute left-0 top-full z-50 w-[560px] translate-y-2 rounded-lg border border-[color:var(--line-dark)] bg-[color:var(--slate)] p-3 opacity-0 shadow-2xl transition-[opacity,transform] duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
      <div className="grid grid-cols-2 gap-1">
        {items.map((it) => (
          <Link key={it.slug} to={`/hizmetler/${it.slug}`} className="rounded px-3 py-2 text-sm text-zinc-300 transition-colors hover:bg-white/5 hover:text-white" data-testid={`nav-dd-${it.slug}`}>
            {it.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function Logo() {
  const { settings: s } = useSite();
  return (
    <Link to="/" className="flex shrink-0 items-center gap-3" data-testid="header-logo">
      <img src={mediaUrl(s.logo_main) || "/assets/logo-shield.webp"} alt="Rüzgar Özel Güvenlik logosu" className="h-11 w-auto sm:h-12" width="40" height="52" />
      <span className="leading-none">
        <span className="block font-display text-[15px] font-extrabold tracking-wide text-white sm:text-base">RÜZGAR</span>
        <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Özel Güvenlik</span>
      </span>
    </Link>
  );
}

function DesktopNav() {
  const { services, sectors } = useSite();
  return (
    <nav className="hidden items-center lg:flex" aria-label="Ana menü">
      {NAV.map((n) => {
        if (!n.menu) {
          return <NavLink key={n.to} to={n.to} end className={linkCls} data-testid={`nav-${n.to.slice(1) || "home"}`}>{n.label}</NavLink>;
        }
        return (
          <div key={n.to} className="group relative">
            <NavLink to={n.to} className={linkCls} data-testid={`nav-${n.to.slice(1)}`}>
              <span className="inline-flex items-center gap-1">{n.label} <ChevronDown className="h-3 w-3" /></span>
            </NavLink>
            <Dropdown items={n.menu === "services" ? services : sectors} />
          </div>
        );
      })}
    </nav>
  );
}

const mobileCls = ({ isActive }) => `border-b border-white/5 py-3.5 text-base font-semibold ${isActive ? "text-[color:var(--red)]" : "text-white"}`;

function MobileMenu() {
  return (
    <nav className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-white/10 bg-[color:var(--ink)] lg:hidden" data-testid="mobile-menu">
      <div className="container-x flex flex-col py-3">
        {NAV.map((n) => <NavLink key={n.to} to={n.to} end className={mobileCls} data-testid={`mobile-nav-${n.to.slice(1) || "home"}`}>{n.label}</NavLink>)}
        <Link to="/sikca-sorulan-sorular" className="border-b border-white/5 py-3.5 text-base font-semibold text-white" data-testid="mobile-nav-sss">Sıkça Sorulan Sorular</Link>
        <Link to="/operasyon/giris" className="mt-3 inline-flex items-center justify-center gap-2 rounded-md border border-white/20 py-3 text-base font-semibold text-white" data-testid="mobile-nav-ops-login"><LogIn className="h-5 w-5 text-[color:var(--red)]" /> Personel / Müşteri Girişi</Link>
      </div>
    </nav>
  );
}

function HeaderActions({ phone, open, toggle }) {
  return (
    <div className="flex items-center gap-2">
      <a href={telHref(phone)} className="hidden items-center gap-2 whitespace-nowrap text-sm font-semibold text-white 2xl:flex" data-testid="header-phone">
        <Phone className="h-4 w-4 text-[color:var(--red)]" /> {phone}
      </a>
      <Link to="/operasyon/giris" title="Personel / Müşteri Girişi" className="hidden items-center gap-1.5 whitespace-nowrap rounded-md border border-white/20 px-3 py-2 text-[13px] font-semibold text-white transition-colors hover:border-[color:var(--red)] hover:text-white sm:inline-flex" data-testid="header-ops-login"><LogIn className="h-4 w-4 text-[color:var(--red)]" /> Giriş</Link>
      <Link to="/teklif-al" className="btn-red whitespace-nowrap !px-4 !py-2.5" data-testid="header-quote-btn">Teklif Al</Link>
      <button className="rounded-md p-2 text-white lg:hidden" onClick={toggle} aria-label="Menüyü aç" data-testid="mobile-menu-toggle">
        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>
    </div>
  );
}

export function Header() {
  const { settings: s } = useSite();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0C]/95 backdrop-blur-md" data-testid="site-header">
      <div className="container-x flex h-16 items-center justify-between gap-4 sm:h-[72px]">
        <Logo />
        <DesktopNav />
        <HeaderActions phone={s.phone} open={open} toggle={() => setOpen(!open)} />
      </div>
      {open && <MobileMenu />}
    </header>
  );
}
